import { OptionsItemLayoutData, OptionsItemLiveData, OptionsItemLocData, OptionsItemThemeData, TextStatesData, TextureStatesData } from "../../../types/types";
import { ApplyMixins } from "../../../utils/ApplyMixins";
import { Interactive } from "../../../mixins/Interactive";
import { Showable } from "../../../mixins/Showable";
import { Toggleable } from "../../../mixins/Toggleable";
import { UpdateView } from "../../../mixins/UpdateView";
import { ISize } from "../../../controls/scrollpane/ISize";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { RectSize } from "../../../utils/RectSize";
import { ButtonBase, ButtonEvents } from "../../../ui/ButtonBase";
import { OptionsItemBase } from "./OptionsItemBase";
import isMobile from 'ismobilejs';

// get ts linting
export interface OptionsToggleItem extends Showable, Interactive, Toggleable, UpdateView { }


export class OptionsToggleItem
    extends OptionsItemBase
    implements ISize {
    /**
     * Index of this item in parent @see OptionsCategoryContent
     */
    // public index: number;
    public btnHelp: ButtonBase;
    public toggleIcon: Phaser.GameObjects.Image;

    private static appliedMixins: boolean = false;

    protected toggleOnIconStatesTextureDatas: TextureStatesData = {
        out: { texture: LoaderFileKeys.ui, frame: "icon-toggle-on.png" },
        over: { texture: LoaderFileKeys.ui, frame: "icon-toggle-on.png" },
        disabled: { texture: LoaderFileKeys.ui, frame: "icon-toggle-on-disabled.png" },
    }
    protected toggleOffIconStatesTextureDatas: TextureStatesData = {
        out: { texture: LoaderFileKeys.ui, frame: "icon-toggle-off.png" },
        over: { texture: LoaderFileKeys.ui, frame: "icon-toggle-off.png" },
        disabled: { texture: LoaderFileKeys.ui, frame: "icon-toggle-off-disabled.png" },
    }

    // callbacks back to parent for resolution
    protected onItemToggleChange: Function;
    protected onItemHelpClick: Function;


    public Init(
        // itemIndex: number,
        bgTextureStates: TextureStatesData = undefined,
        labelTextStates: TextStatesData = undefined,
        bgTextureToggleStates: TextureStatesData = undefined,
        labelTextToggleStates: TextStatesData = undefined,
        onItemToggleChange: Function = undefined,
        onItemHelpClick: Function = undefined,
        delimiterLineAlpha: number,
    ): void {
        if (!OptionsToggleItem.appliedMixins) {
            ApplyMixins(OptionsToggleItem, [Showable, Interactive, Toggleable, UpdateView]);
            OptionsToggleItem.appliedMixins = true;
        }

        // this.index = itemIndex;
        // this.name = "OptionsToggleItem";
        this.onItemToggleChange = onItemToggleChange;
        this.onItemHelpClick = onItemHelpClick;
        this.delimiterLineAlpha = delimiterLineAlpha;

        // use this when deciding exact item size @see parent->OnResize
        // this.type = "OptionsToggleItem";
        this.updateViews = [];
        this.size = new RectSize();
        // set default (mixin) props
        // NOTE: default values set during mixin declaration are undefined)
        // Showable
        this.isShowing = false;
        // Interactive
        this.isInteractive = true;
        // Toggleable
        this.isSelected = false;
        // UpdateView
        this.pointerOver = false;

        const _isMobile: boolean = isMobile(window.navigator).any;

        this.bg = this.scene.make.image({ key: bgTextureStates.out.texture, frame: bgTextureStates.out.frame });
        this.bg.setOrigin(0);
        this.add(this.bg);

        this.imgDelimiterLine = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "rect-2x2-white.png" });
        this.imgDelimiterLine.setOrigin(0, 0.5);
        this.imgDelimiterLine.alpha = this.delimiterLineAlpha;
        this.add(this.imgDelimiterLine);

        this.label = this.scene.make.text({
            // text: label, 
            style: labelTextStates.out,
        });
        // this.label.setOrigin(0, 0.5);
        this.label.setOrigin(0, 0.5);
        this.add(this.label);

        this.btnHelp = new ButtonBase(this.scene);
        this.btnHelp.InitBase(
            _isMobile ?
                {
                    out: { texture: LoaderFileKeys.ui, frame: "btn-options-help-out.png" },
                    // over: { texture: LoaderFileKeys.ui, frame: "btn-options-help-over.png" }
                } :
                {
                    out: { texture: LoaderFileKeys.ui, frame: "btn-options-help-out.png" },
                    over: { texture: LoaderFileKeys.ui, frame: "btn-options-help-over.png" }
                }
        );
        this.btnHelp.PostInit(30);
        this.add(this.btnHelp);

        this.toggleIcon = this.scene.make.image({ x: 200, y: 0, key: LoaderFileKeys.ui, frame: this.toggleOnIconStatesTextureDatas.out.frame });
        this.toggleIcon.setScale(0.8);
        this.toggleIcon.setOrigin(0.5);
        this.add(this.toggleIcon);

        if (!_isMobile) {
            this.AddViewDef({
                view: this.bg,
                textureStates: bgTextureStates,
                textureToggleStates: bgTextureToggleStates,
            });

            this.AddViewDef({
                view: this.label,
                textStates: labelTextStates,
                textToggleStates: labelTextToggleStates,
            });
        }

        this.AddViewDef({
            view: this.toggleIcon,
            textureStates: this.toggleOffIconStatesTextureDatas,
            textureToggleStates: this.toggleOnIconStatesTextureDatas,
        });

        // this.SetHitAreaFromViews([this.bg, this.toggleIcon], 0);
        this.SetHitAreaFromViews([this.bg], 0);

        // internal mixin over/out
        // this.AddListeners();
    }

    /**
     * NOTE: called after getting this item from pool or new, in parent @see OptionsCategoryContent
     */
    public override RegisterListeners(): void {
        // console.log("RegisterListeners");

        // super.RegisterListeners(); // trace all at once (prettier output)
        this.AddListeners();

        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnClick, this);
        this.btnHelp.on(ButtonEvents.CLICK, this.OnBtnHelpClick, this);
    }

    /**
     * NOTE: called before pooling this item, in parent @see OptionsCategoryContent
     */
    public override UnregisterListeners(): void {
        // console.log("UnregisterListeners");

        // super.UnregisterListeners(); // trace all at once (prettier output)
        this.RemoveListeners();

        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnClick);
        this.btnHelp.off(ButtonEvents.CLICK, this.OnBtnHelpClick);
    }

    /**
     * Execute click externally
     */
    public Click(): void {
        this.OnClick();
    }

    protected OnClick(): void {
        // change view to opposite of current selected @see isSelected
        this.Toggle();
        const newValue: boolean = this.isSelected;
        // change data model (live JSON)
        this.itemLiveData.value = newValue;
        // dispatch change
        if (this.onItemToggleChange != undefined) {
            this.onItemToggleChange(this, newValue);
        }
    }

    protected OnBtnHelpClick(): void {
        // dispatch change
        if (this.onItemHelpClick != undefined) {
            this.onItemHelpClick(
                this.itemLayoutData,
                this.itemThemeData,
                this.itemLocData,
                this.itemLiveData,
            );
        }
    }

    /**
     * Data model(s) changed. Update view.
     * 
     * Implicit (@see SetData) or explicit (this) call to update from either:
     * 1) localization (texts changed) or 
     * 2) live data (value indexes changed) change
     * 
     * NOTE: The 'Data' in 'DataChange' means any data, eg live data, 
     * localization data or any other data this component uses.
     */
    public override UpdateViewFromDataChange(
        itemLocData: OptionsItemLocData = undefined,
        itemLiveData: OptionsItemLiveData = undefined,
    ): void {
        // console.log("UpdateViewFromDataChange");
        // console.log(itemLocData.itemLabel, itemLiveData);

        this.imgDelimiterLine.visible = this.hasDelimiterLine;

        if (itemLocData != undefined) {
            this.label.text = itemLocData.itemLabel;
            if (itemLiveData != undefined) {
                const newSelected = itemLiveData.value as boolean;
                // if (this.isSelected != newSelected) {
                //     // toggle on/off
                //     this.SetSelected(newSelected, true); // force?
                // }
                this.SetSelected(newSelected);
            }
            const hasHelp = itemLocData.help == undefined ? false : true;
            this.btnHelp.visible = hasHelp;
            // console.log(itemLocData.id, hasHelp);
            // console.log(this.btnHelp.x);
        }
    }

    /**
     * @alike: OnResize
     * Uses 'maximum allowed width' to compute item's resulting height
     * - Item's width and height depends on height of label textfield
     * - label's height depends on amount of HORIZONTAL space and maximum horizontal space allowed for item
     * 
     * @param width 
     */
    public override SetWidthGetHeight(width: number): number {
        // console.log("SetWidthGetHeight");

        // total size incl extra space that the help btn would take..
        const btnHelpWidthInclSpacing = this.btnHelp.visible ? this.btnHelp.displayWidth + this.itemSpacing.width : 0;
        const btnHelpHeight = this.btnHelp.visible ? this.btnHelp.displayHeight : 0;

        // resizes label textfield
        this.label.style.setWordWrapWidth(width - 2 * this.borderPadding.horizontal - this.itemSpacing.width - this.toggleIcon.displayWidth - btnHelpWidthInclSpacing);

        // computes and returns height
        const height = Math.max(
            this.label.displayHeight + this.borderPadding.top + this.borderPadding.bottom,
            this.toggleIcon.displayHeight + this.borderPadding.top + this.borderPadding.bottom,
            btnHelpHeight + this.borderPadding.top + this.borderPadding.bottom
        );

        return height;
    }

    /**
     * At this point we know this item's width and height.
     * 
     * Triggered via: 
     * > OptionsScreen.OnResize (OptionsScreen.OnResize)
     *   > OptionsContentScroll.SetSize
     *     > OptionsCategoryContent.SetWidth
     *       > ScrollPaneTitledListContent.SetWidth
     *         > super.SetSize
     *           > super.OnResize
     */
    public override OnResize(): void {
        // console.log("OnResize");

        const centerY = this.size.height / 2;

        this.bg.setDisplaySize(this.size.width, this.size.height);

        // delimiter (if item has it -- last item in list does not have it)
        if (this.hasDelimiterLine) {
            // origin 0,1 (bot left)
            this.imgDelimiterLine.y = this.size.height;
            this.imgDelimiterLine.displayWidth = this.size.width;
        }

        // label origin (0, 0.5)
        this.label.setPosition(this.borderPadding.horizontal, centerY);
        // NOTE: after label
        if (this.btnHelp.visible) {
            this.btnHelp.setPosition(this.label.x + this.label.displayWidth + this.itemSpacing.width + this.btnHelp.displayWidth / 2, centerY);
            // this.btnHelp.x = this.label.x + this.label.displayWidth + this.itemSpacing.width + this.btnHelp.displayWidth/2;
        }
        // toggleIcon origin 0.5
        this.toggleIcon.setPosition(this.size.width - this.borderPadding.horizontal - this.toggleIcon.displayWidth / 2, centerY);

        this.SetHitAreaRectangle(new Phaser.Geom.Rectangle(0, 0, this.size.width, this.size.height));
    }
}